import React, { useState, useCallback, useEffect } from "react";
import SingleOTPInput from "./singleOTPInput";
import timerIcon from "../../../assets/doctorLoginAssets/timer_FILL0_wght400_GRAD0_opsz24.svg";
import "./otpInput.scss";

const OTPInput = (props) => {
  const {
    length = 6,
    isNumberInput,
    timerUnit,
    hasInputError,
    inputErrorMsg,
    hideTimerResendBtn,
    autoFocus,
    disabled,
    onChangeOTP,
    inputStyle,
    hasError,
    resetOtp,
    errorMessage,
    handleResendOTP,
    setResetOtp,
    onEnter,
  } = props;

  const [activeInput, setActiveInput] = useState(0);
  const [otpValues, setOTPValues] = useState("");
  const [minutes, setMinutes] = useState(timerUnit ? timerUnit : 2);
  const [seconds, setSeconds] = useState(0);
  const [disableResendButton, setDisableResendButton] = useState(true);
  const [startTimer, seStartTimer] = useState(true);
  const validationPattern = /\d{1}/;

  useEffect(() => {
    if (startTimer) {
      const interval = setInterval(() => {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        }
        if (seconds === 0) {
          if (minutes === 0) {
            clearInterval(interval);
          } else {
            setSeconds(59);
            setMinutes(minutes - 1);
          }
        }
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [seconds, startTimer]);

  useEffect(() => {
    if (resetOtp) {
      setOTPValues("");
      setTimeout(() => {
        setResetOtp(false);
      }, 10);
    }
  }, [resetOtp]);

  useEffect(() => {
    if (seconds == 0 && minutes == 0) {
      seStartTimer(false);
      setDisableResendButton(false);
    } else {
      setDisableResendButton(true);
    }
  }, [seconds, minutes]);

  // Function to resend OTP
  const handleResend = () => {
    setMinutes(timerUnit ? timerUnit : 2);
    setSeconds(0);
    seStartTimer(true);
    handleResendOTP && handleResendOTP();
  };

  // Helper to return OTP from inputs
  const handleOtpChange = useCallback(
    (otp) => {
      const otpValue = otp.join("");
      onChangeOTP(otpValue);
    },
    [onChangeOTP]
  );

  // Helper to return value with the right type: 'text' or 'number'
  const getRightValue = useCallback(
    (str) => {
      let changedValue = str;
      if (!validationPattern.test(changedValue) && changedValue !== "") return;
      if (!isNumberInput || !changedValue) {
        return changedValue;
      }

      return Number(changedValue) >= 0 ? changedValue : "";
    },
    [isNumberInput]
  );

  // Change OTP value at focussing input
  const changeCodeAtFocus = useCallback(
    (str) => {
      const updatedOTPValues = [...otpValues];
      updatedOTPValues[activeInput] = str[0] || "";
      setOTPValues(updatedOTPValues);
      handleOtpChange(updatedOTPValues);
    },
    [activeInput, handleOtpChange, otpValues]
  );

  // Focus `inputIndex` input
  const focusInput = useCallback(
    (inputIndex) => {
      const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0);
      setActiveInput(selectedIndex);
    },
    [length]
  );

  const focusPrevInput = useCallback(() => {
    focusInput(activeInput - 1);
  }, [activeInput, focusInput]);

  const focusNextInput = useCallback(() => {
    focusInput(activeInput + 1);
  }, [activeInput, focusInput]);

  // Handle onFocus input
  const handleOnFocus = useCallback(
    (index) => () => {
      focusInput(index);
    },
    [focusInput]
  );

  // Handle onChange value for each input
  const handleOnChange = useCallback(
    (e) => {
      const val = getRightValue(e.currentTarget.value);
      if (!val) {
        e.preventDefault();
        return;
      }
      changeCodeAtFocus(val);
      focusNextInput();
    },
    [changeCodeAtFocus, focusNextInput, getRightValue]
  );

  // Handle onBlur input
  const onBlur = useCallback(() => {
    setActiveInput(-1);
  }, []);

  // Handle onKeyDown input
  const handleOnKeyDown = useCallback(
    (e) => {
      const pressedKey = e.key;

      switch (pressedKey) {
        case "Backspace":
          changeCodeAtFocus("");
          focusPrevInput();
          break;
        case "Delete": {
          changeCodeAtFocus("");
          break;
        }
        case "ArrowLeft": {
          e.preventDefault();
          focusPrevInput();
          break;
        }
        case "ArrowRight": {
          e.preventDefault();
          focusNextInput();
          break;
        }
        case "Enter": {
          e.preventDefault();
          onEnter && onEnter();
          break;
        }
        default: {
          if (
            pressedKey.match(/^[^a-zA-Z0-9]$/) ||
            e.key?.toLowerCase() === "e" ||
            e.key === "+" ||
            e.key === "-" ||
            e.key === "." ||
            e.key === "ArrowUp" ||
            e.key === "ArrowDown"
          ) {
            e.preventDefault();
          }

          break;
        }
      }
    },
    [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, otpValues]
  );

  const handleOnPaste = useCallback(
    (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData
        .getData("text/plain")
        .trim()
        .slice(0, length - activeInput)
        .split("");
      if (pastedData) {
        let nextFocusIndex = 0;
        const updatedOTPValues = [...otpValues];
        updatedOTPValues.forEach((val, index) => {
          if (index >= activeInput) {
            const changedValue = getRightValue(pastedData.shift() || val);
            if (changedValue) {
              updatedOTPValues[index] = changedValue;
              nextFocusIndex = index;
            }
          }
        });
        setOTPValues(updatedOTPValues);
        setActiveInput(Math.min(nextFocusIndex + 1, length - 1));
        handleOtpChange(updatedOTPValues);
      }
    },
    [activeInput, getRightValue, length, otpValues]
  );

  const ResendOTPTimerUI = () => {
    return (
      <div className="oi-resend-timer-container">
        <img src={timerIcon} alt="#" />
        <span className="oi-resend-time">
          {minutes < 10 ? `0${minutes}` : minutes}:
          {seconds < 10 ? `0${seconds}` : seconds}
        </span>
      </div>
    );
  };

  const ResendOTPButton = () => {
    return (
      <div
        className={
          "oi-resend-button " +
          (disableResendButton ? "oi-resend-button-disabled " : "")
        }
        onClick={() => !disableResendButton && handleResend()}
      >
        Resend OTP
      </div>
    );
  };

  const ResendTimerWithButtonUI = () => {
    return (
      <div className="oi-resend-time-with-button">
        <div className="otp-timer-and-otp-error-wrong-msg-wrapper">
          {!hideTimerResendBtn && ResendOTPTimerUI()}
          {hasError && (
            <div className="oi-otp-input-error-content">{errorMessage}</div>
          )}
        </div>
        {!hideTimerResendBtn && ResendOTPButton()}
      </div>
    );
  };

  const errorMsgUI = () => {
    return (
      <div>
        {hasInputError && (
          <div className="oi-otp-error-content">{inputErrorMsg}</div>
        )}
      </div>
    );
  };

  return (
    <div className="otp-input-wrapper">
      <div className="oiw-input-container">
        <div className="oi-input-container">
          {Array(length)
            .fill("")
            .map((_, index) => (
              <SingleOTPInput
                key={`SingleInput-${index}`}
                type={"number"}
                focus={activeInput === index}
                value={otpValues && otpValues[index]}
                autoFocus={autoFocus}
                onFocus={handleOnFocus(index)}
                onChange={handleOnChange}
                onKeyDown={handleOnKeyDown}
                onBlur={onBlur}
                onPaste={handleOnPaste}
                style={inputStyle}
                className={
                  "oi-otp-input " + (hasError ? "oi-otp-input-error" : "")
                }
                disabled={disabled}
                placeholder="*"
              />
            ))}
        </div>
      </div>
      <div className="oi-otp-button">{errorMsgUI()}</div>
      <div className="oi-otp-button">{ResendTimerWithButtonUI()}</div>
    </div>
  );
};

export default OTPInput;
